import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";

import { IntlProvider } from "react-intl";

import languagesConfig, {
  ILanguage,
  LanguagesCodes,
} from "../i18n/language.config";

import Hebrew from "../i18n/languages/he.json";
import English from "../i18n/languages/en.json";
import { AppScopes } from "../models/app-scopes.model";
import { useSearchParams } from "react-router-dom";
import { Constants } from "../constants";

export enum TYPES {
  SET_NEW_LANGUAGE = "SET_NEW_LANGUAGE",
  ADD_SCOPES = "ADD_SCOPES",
  REMOVE_SCOPES = "REMOVE_SCOPES",
}

interface AppReducerState {
  language: ILanguage;
  scopes?: AppScopes[];
}

type AppReducerAction =
  | { type: TYPES.SET_NEW_LANGUAGE; payload: ILanguage }
  | { type: TYPES.REMOVE_SCOPES; payload: AppScopes[] }
  | { type: TYPES.ADD_SCOPES; payload: AppScopes[] };

const appReducer = (
  state: AppReducerState,
  action: AppReducerAction
): AppReducerState => {
  switch (action.type) {
    case TYPES.SET_NEW_LANGUAGE: {
      return {
        ...state,
        language: languagesConfig
          .filter((language) => language.code === action.payload.code)
          .pop()!,
      };
    }
    case TYPES.ADD_SCOPES:
      if (
        !action.payload?.length ||
        // If new scope already in scopes, don't add nothing
        state.scopes?.some((scope) => action.payload.includes(scope))
      )
        return state;

      return {
        ...state,
        scopes: Array.from(
          new Set([...(state.scopes ?? []), ...action.payload])
        ),
      };

    case TYPES.REMOVE_SCOPES:
      if (
        !action.payload?.length ||
        // If new scope is not in scopes, don't remove nothing
        !state.scopes?.some((scope) => action.payload.includes(scope))
      )
        return state;

      return {
        ...state,
        scopes: state.scopes?.filter(
          (scope) => !action.payload.includes(scope)
        ),
      };
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

interface IAppContext {
  state: AppReducerState;
  dispatch: Dispatch<AppReducerAction>;
}

const initialReducerState: AppReducerState = {
  language: languagesConfig[0],
};

const AppContext = React.createContext<IAppContext>({
  state: initialReducerState,
  dispatch: () => undefined,
});

const AppContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [state, dispatch] = useReducer(appReducer, initialReducerState);

  const [queryParams] = useSearchParams();

  useEffect(() => {
    // Detect "uniqueId" app scope
    const queryUniqueId = queryParams.get(Constants.UNIQUE_ID_QUERY_PARAM_NAME);

    !!queryUniqueId || queryUniqueId === ""
      ? dispatch({ type: TYPES.ADD_SCOPES, payload: ["uniqueId"] })
      : dispatch({ type: TYPES.REMOVE_SCOPES, payload: ["uniqueId"] });
  }, [queryParams]);

  let translation;
  switch (state.language.code) {
    case LanguagesCodes.he:
      translation = Hebrew;
      break;

    case LanguagesCodes.en:
      translation = English;
      break;

    default:
      translation = English;
      break;
  }

  return (
    <AppContext.Provider value={{ dispatch, state }}>
      <IntlProvider messages={translation} locale={state.language.code}>
        <div className={`${state.language.direction} w-100 h-100`}>
          {children}
        </div>
      </IntlProvider>
    </AppContext.Provider>
  );
};

const useAppContext = () => useContext(AppContext);

export { AppContextProvider, useAppContext };
